<template>
  <div class="reg-split">
    <AuthBrandPanel
      kick="You're invited"
      heading="Join your team on <em>Yorlin</em>."
      sub="Set a password and you're in — your teammates' pipelines, prospects and campaigns are waiting."
      foot="Made in the Cotswolds · Companies House native"
    />

    <div class="auth-form">
      <div class="af-inner" style="max-width:400px">
        <!-- loading -->
        <template v-if="loading">
          <div class="big-state">
            <div class="bs-ic mail"><CygnusIcon name="spinner" :spin="true" /></div>
            <h3>Checking your invite…</h3>
          </div>
        </template>

        <!-- invalid / expired -->
        <template v-else-if="!invite">
          <div class="big-state">
            <div class="bs-ic warn"><CygnusIcon name="clock" /></div>
            <h3>This invite isn't valid</h3>
            <p>{{ error || 'This invite is invalid, already used, or has expired. Ask whoever invited you to send a fresh one.' }}</p>
            <div class="resend"><a href="/login">← Back to sign in</a></div>
          </div>
        </template>

        <!-- accepted -->
        <template v-else-if="done">
          <div class="big-state">
            <div class="bs-ic ok"><CygnusIcon name="check-circle" /></div>
            <h3>You're in!</h3>
            <p>Welcome to <b>{{ invite.workspace }}</b>. Taking you to your dashboard…</p>
          </div>
        </template>

        <!-- accept form -->
        <template v-else>
          <div class="af-h">Join {{ invite.workspace }}</div>
          <div class="af-sub">
            You've been invited as a <b>{{ invite.role_label }}</b>. Set a password for <b>{{ invite.email }}</b> to get started.
          </div>

          <div v-if="banner" class="fbanner err">
            <span class="fb-ic"><CygnusIcon name="warning" /></span>
            <span>{{ banner }}</span>
          </div>

          <form @submit.prevent="submit">
            <div class="field">
              <div class="fl">Your name <span class="opt">· optional</span></div>
              <div class="input">
                <span class="ic"><CygnusIcon name="user" /></span>
                <input v-model.trim="name" class="val" type="text" placeholder="Your full name" autocomplete="name" />
              </div>
            </div>

            <div class="field">
              <div class="fl">Choose a password</div>
              <div class="input">
                <span class="ic"><CygnusIcon name="lock" /></span>
                <input v-model="password" class="val" :type="showPw ? 'text' : 'password'" placeholder="At least 10 characters" autocomplete="new-password" />
                <span class="eye" role="button" tabindex="0" @click="showPw = !showPw" @keyup.enter="showPw = !showPw">
                  <CygnusIcon name="eye" />
                </span>
              </div>
              <div v-if="password" class="pw-strength">
                <div class="pw-bars"><i v-for="n in 4" :key="n" :class="barClass(n)"></i></div>
                <div class="pw-lbl"><span>{{ pw.hint }}</span><b :class="pw.labelClass">{{ pw.label }}</b></div>
              </div>
            </div>

            <button class="btn btn-pri" type="submit" :disabled="submitting">
              <CygnusIcon name="spinner" :spin="true" v-if="submitting" />
              {{ submitting ? 'Setting up…' : 'Join & set my password →' }}
            </button>
          </form>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import AuthBrandPanel from '@root/components/ui/AuthBrandPanel.vue';
import useTokenStore from '@apexl/icarus-core/src/store/useTokenStore';

/* global BACKEND_DOMAIN */

// Public invite-accept surface (onboarding slice 2b). Same interceptor-free
// fetch pattern as RegistrationFlow: unauthenticated, token-gated, every status
// read as data (no auth redirect).
export default {
  name: 'InviteAccept',
  components: { AuthBrandPanel },
  data() {
    return {
      tokens: useTokenStore(),
      loading: true,
      invite: null,
      error: '',
      name: '',
      password: '',
      showPw: false,
      submitting: false,
      banner: '',
      done: false,
    };
  },
  computed: {
    token() {
      const m = String(this.$route.path || '').match(/^\/invite\/([^/]+)/);
      return m ? decodeURIComponent(m[1]) : '';
    },
    pw() {
      return this.evaluatePassword(this.password);
    },
  },
  created() {
    this.inspect();
  },
  methods: {
    async apiGet(path) {
      try {
        const res = await fetch(BACKEND_DOMAIN + path, { method: 'GET', credentials: 'include', headers: { Accept: 'application/json' } });
        return await res.json().catch(() => ({}));
      } catch (e) { return { ok: false, error: 'network' }; }
    },
    async apiPost(path, body) {
      try {
        const res = await fetch(BACKEND_DOMAIN + path, {
          method: 'POST', credentials: 'include',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        return await res.json().catch(() => ({}));
      } catch (e) { return { ok: false, error: 'network' }; }
    },
    async inspect() {
      this.loading = true;
      const d = await this.apiGet('/api/v1/invite/' + encodeURIComponent(this.token));
      if (d.ok) { this.invite = d; } else { this.error = d.error || ''; this.invite = null; }
      this.loading = false;
    },
    async submit() {
      this.banner = '';
      if (this.pw.score < 2 || this.password.length < 10) {
        this.banner = 'Use at least 10 characters with a mix of cases, a number or symbol.';
        return;
      }
      this.submitting = true;
      const d = await this.apiPost('/api/v1/invite/' + encodeURIComponent(this.token), { password: this.password, name: this.name });
      if (d.ok) {
        if (d.access_token) this.tokens.setAccessToken(d.access_token);
        if (d.refresh_token) this.tokens.setRefreshToken(d.refresh_token);
        this.done = true;
        setTimeout(() => this.$router.push(d.redirect || '/'), 900);
      } else {
        this.banner = d.error || 'We couldn\'t complete your invite. Please try again.';
        this.submitting = false;
      }
    },
    evaluatePassword(pw) {
      const len = pw ? pw.length : 0;
      let classes = 0;
      if (/[a-z]/.test(pw)) classes++;
      if (/[A-Z]/.test(pw)) classes++;
      if (/[0-9]/.test(pw)) classes++;
      if (/[^A-Za-z0-9]/.test(pw)) classes++;
      let score = classes;
      if (len >= 14) score++;
      if (len < 10) score = Math.min(score, 1);
      score = Math.max(0, Math.min(4, score));
      let label = 'Weak', labelClass = '';
      if (score >= 4) { label = 'Strong'; labelClass = 'strong'; }
      else if (score === 3) { label = 'Good'; labelClass = 'mid'; }
      else if (score === 2) { label = 'Fair'; labelClass = 'mid'; }
      let hint;
      if (len < 10) hint = `${len} character${len === 1 ? '' : 's'} · use at least 10`;
      else if (classes < 3) hint = `${len} characters · add a symbol to strengthen`;
      else hint = `${len} characters · mixed case · number`;
      return { score, label, labelClass, hint };
    },
    barClass(n) {
      const map = { 1: 'f1', 2: 'f2', 3: 'f3', 4: 'f4' };
      return n <= this.pw.score ? map[this.pw.score] : '';
    },
  },
};
</script>

<style scoped>
.reg-split { display: grid; grid-template-columns: 44% 56%; min-height: 100vh; background: #fff; }
.auth-form { background: #fff; padding: 44px 48px; display: flex; flex-direction: column; justify-content: center; }
.af-inner { width: 100%; margin: 0 auto; }
@media (max-width: 980px) {
  .reg-split { grid-template-columns: 1fr; }
  .reg-split :deep(.auth-brand) { display: none; }
  .auth-form { padding: 32px 22px; }
}
</style>
